import { useEffect, useState } from "react";
import { getData, deleteData } from "../../Services/apiService";
import { useAlert } from "../../Provider/Alert/AlertContext";
import { useTranslation } from "react-i18next";

export function useClient() {
    const { addAlert } = useAlert();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [clients, setClients] = useState([]);
    const [search, setSearch] = useState("");
    useEffect(() => {
        fetchListClients();
        // eslint-disable-next-line
    }, [search]);

    /**
     * Fetches a list of clients from the API using query parameters stored in localStorage.
     * Updates localStorage with pagination and total items information.
     * Sets the clients state with the fetched data and handles loading state.
     * Displays an alert if the API response is not successful.
     *
     * @async
     * @function fetchListClients
     * @returns {Promise<void>} Resolves when the client list has been fetched and state updated.
     */
    const fetchListClients = async () => {
        // Get the query string from the local storage
        let queryString = JSON.parse(localStorage.getItem("clients"));
        if (!queryString) {
            queryString = {
                page: 1,
                limit: 10,
                totalItems: 0,
                order: [{ field: "name", direction: "asc" }],
            };
            localStorage.setItem("clients", JSON.stringify(queryString));
        }

        // Define the URI
        let uri = `/client?page=${queryString.page}&limit=${queryString.limit}`;

        // Adicionar ordenações à URI
        const orderString = encodeURIComponent(
            JSON.stringify(queryString.order)
        );
        uri += `&order=${orderString}`;

        // Adicionar filtros à URI
        if (search !== "") {
            const searchString = encodeURIComponent(
                JSON.stringify([
                    {
                        field: "name",
                        operator: "like",
                        value: `%${search}%`,
                        logic_operator: "OR",
                        group_start: true,
                    },
                    {
                        field: "last_name",
                        operator: "like",
                        value: `%${search}%`,
                        logic_operator: "OR",
                    },
                    {
                        field: "cpf_cnpj",
                        operator: "like",
                        value: `%${search}%`,
                        group_end: true,
                    },
                ])
            );
            uri += `&where=${searchString}`;
        }

        // Get the data from the API
        const getClients = getData(uri);
        getClients.then((response) => {
            console.log("Fetch clients response:", response);
            if (response.status === 200) {
                console.log(response.body);
                setClients(response.body.data);
                queryString.lastPage = response.body.total_pages;
                queryString.totalItems = response.body.total_items;
                localStorage.setItem("clients", JSON.stringify(queryString));
                setLoading(false);
                return;
            }
            addAlert("danger", t("client.alert.error"), response.body);
        });
    };

    // Delete the client
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [itemDelete, setItemDelete] = useState(null);

    const deleteClientConfirm = (client) => {
        // Mostrar o modal de confirmação
        setItemDelete(client);
        setShowConfirmDialog(true);
    };

    const deleteCancel = () => {
        setShowConfirmDialog(false); // Fechar o modal sem excluir
    };

    // Confirm the exclusion
    const deleteClient = async () => {
        // Excluir o item

        try {
            // Chamar a API para excluir o item
            const deleteItem = await deleteData(`/client`, itemDelete.id);

            // Verificar a resposta da API
            if (deleteItem.status === 204) {
                addAlert(
                    "success",
                    t("client.delete.success.title"),
                    t("client.delete.success.message")
                );
                setShowConfirmDialog(false);
                fetchListClients();
                return;
            }

            console.log("Delete client response:", deleteItem);

            // Tratar erros específicos
            if (deleteItem.status === 409) {
                addAlert(
                    "warning",
                    t("client.delete.error.title"),
                    t("client.delete.error.409")
                );
                return;
            }

            addAlert(
                "danger",
                t("client.delete.error.title"),
                t("client.delete.error.message")
            );
        } catch (error) {
            addAlert(
                "danger",
                t("client.delete.error.title"),
                t("client.delete.error.message")
            );
        }
    };

    return {
        loading,
        clients,
        fetchListClients,
        setSearch,
        showConfirmDialog,
        itemDelete,
        deleteClient,
        deleteClientConfirm,
        deleteCancel,
    };
}
